<template>
  <v-dialog
    no-click-animation
    persistent
    content-class="dialog"
    max-width="1140px"
    v-model="dialog"
    fullscreen
    :retain-focus="false"
    hide-overlay
    dark
    transition="dialog-bottom-transition">
    <v-dialog v-model="showFreeTrainingWelcome" fullscreen hide-overlay persistent transition="fade-transition">
      <div class="welcome-overlay">
        <div class="welcome-content">
          <div class="emoji-line">💪</div>
          <h1 class="welcome-title">
            <span class="line1">Let's</span><br />
            <span class="line2">Go!</span>
          </h1>
          <p class="welcome-subtitle">Dein freies Training ist startklar. Füge jetzt deine erste Übung hinzu.</p>
          <v-btn x-large rounded depressed color="white" class="font-weight-bold black--text mt-8 start-btn" @click="openAddExerciseFromWelcome">
            <v-icon left>mdi-plus-circle</v-icon>
            Erste Übung auswählen
          </v-btn>
        </div>
      </div>
    </v-dialog>
    <div
      class="mainpage-top premium-header-shadow"
      :class="{ 'hyrox-mode-active': activeSplit && activeSplit.isHyrox }"
      :style="{
        height: overWrittenDivHeight + 'px',
        width: '100vw',
        maxWidth: '520px',
        background: getHeaderBackground(),
        borderBottomLeftRadius: '24px',
        borderBottomRightRadius: '24px',
        '--split-color': currentBackgroundColorHex,
        '--split-color-rgb': splitColorRgb,
        '--border-color': isBackgroundDark() ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
        '--shadow-color': isBackgroundDark() ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.1)',
        '--text-color': getTextColorBasedOnBackground(),
      }">
      <!-- Glass Overlay -->
      <div class="header-glass-overlay"></div>

      <v-row no-gutters align="center" class="px-4 py-2 header-row-base" style="position: relative; z-index: 2">
        <v-col cols="auto">
          <v-btn icon @click="onClose()" :ripple="false" class="glass-btn">
            <v-icon :color="getTextColorBasedOnBackground()">mdi-chevron-left</v-icon>
          </v-btn>
        </v-col>
        <v-col class="overflow-hidden pl-2 text-center">
          <div
            v-if="supersetIndicator"
            class="caption font-weight-bold text-uppercase"
            :class="[getTextColorBasedOnBackgroundClass()]"
            style="opacity: 0.7; font-size: 0.65rem !important; letter-spacing: 1px">
            {{ supersetIndicator }}
          </div>
          <div
            class="font-weight-black text-truncate text-uppercase header-title"
            :class="[getTextColorBasedOnBackgroundClass()]"
            style="font-size: 1.1rem; letter-spacing: 0.5px; line-height: 1.2">
            {{ getSelectedExercise ? currentExerciseDisplayTitle : activeSplit.title }}
          </div>
          <div
            v-if="getSelectedExercise && getSelectedExercise.deviceShortForm"
            class="caption"
            :class="[getTextColorBasedOnBackgroundClass()]"
            style="opacity: 0.8">
            {{ getSelectedExercise.deviceShortForm }}
          </div>
        </v-col>
        <v-col v-if="watchCompanionAvailable" cols="auto" class="px-0">
          <v-btn
            small
            rounded
            depressed
            class="watch-pulse-trigger"
            :class="{ 'watch-pulse-trigger--live': watchCompanionIsLive }"
            :loading="watchCompanionIsBusy"
            :color="watchCompanionButtonColor"
            @click.stop="startWatchPulseTracking">
            <v-icon small :left="watchCompanionIsLive">mdi-watch-variant</v-icon>
            <span v-if="watchCompanionIsLive">{{ watchCompanionPulseText }}</span>
            <span v-else class="d-none d-sm-inline">Puls</span>
          </v-btn>
        </v-col>
        <v-col v-if="getSelectedExercise && !getSelectedExercise.isSuperset && !activeSplit.isHyrox" cols="2" class="text-right">
          <v-btn icon @click="openSwapDialog" title="Übung austauschen" class="glass-btn">
            <v-icon :color="getTextColorBasedOnBackground()">mdi-swap-horizontal</v-icon>
          </v-btn>
        </v-col>
        <v-col v-else-if="!getSelectedExercise" cols="auto" class="d-flex flex-row justify-content-center">
          <v-btn icon @click="handleOpenAddDialog" title="Übung hinzufügen" class="glass-btn">
            <v-icon :color="getTextColorBasedOnBackground()">mdi-plus</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <div :class="{ 'header-content-wrapper': activeSplit && activeSplit.isHyrox }" style="position: relative; z-index: 2">
        <div
          v-if="activeSplit && activeSplit.isHyrox && !getSelectedExercise"
          class="race-clock-container"
          :class="{ 'animate-in': raceClockStarted }">
          <div class="race-clock-display">{{ raceClockDisplay }}</div>
        </div>
        <v-expand-transition v-else>
          <div v-if="!isExpanded && !getSelectedExercise" class="collapsed-metrics px-4 pb-3 pt-1 d-flex justify-space-between align-center">
            <div class="metric-capsule capsule-kcal" :class="{ 'light-theme': !isBackgroundDark() }">
              <v-icon x-small color="#ff7800" class="mr-1">mdi-fire</v-icon>
              <span class="metric-val font-weight-bold" :class="[getTextColorBasedOnBackgroundClass()]">{{ caloriesBurned }}</span>
              <span class="metric-unit" :class="[getTextColorBasedOnBackgroundClass()]">kcal</span>
            </div>

            <div class="metric-capsule capsule-min" :class="{ 'light-theme': !isBackgroundDark() }">
              <v-icon x-small color="#00b4ff" class="mr-1">mdi-clock-outline</v-icon>
              <span class="metric-val font-weight-bold" :class="[getTextColorBasedOnBackgroundClass()]">{{ workoutMinutes }}</span>
              <span class="metric-unit" :class="[getTextColorBasedOnBackgroundClass()]">Min</span>
            </div>

            <div class="metric-capsule capsule-kg" :class="{ 'light-theme': !isBackgroundDark() }">
              <v-icon x-small color="#00e678" class="mr-1">mdi-weight-lifter</v-icon>
              <span class="metric-val font-weight-bold" :class="[getTextColorBasedOnBackgroundClass()]">{{ liftedWeight }}</span>
              <span class="metric-unit" :class="[getTextColorBasedOnBackgroundClass()]">kg</span>
            </div>
          </div>
        </v-expand-transition>

        <v-expand-transition>
          <button
            v-if="canUseWatchFeature && watchCompanion.status !== 'idle'"
            type="button"
            class="watch-companion-live-strip mx-3 mb-2"
            :class="`watch-zone-${watchCompanion.heartRateZone || 0}`"
            @click="startWatchPulseTracking">
            <span class="watch-live-icon">
              <v-icon small color="#ff4d5e">mdi-heart-pulse</v-icon>
            </span>
            <span class="watch-live-copy">
              <strong>{{ watchCompanionStatusTitle }}</strong>
              <small>{{ watchCompanionStatusDetail }}</small>
            </span>
            <span v-if="watchCompanionIsLive" class="watch-live-value">
              {{ watchCompanionPulseText }} <small>BPM</small>
            </span>
            <v-progress-circular v-else-if="watchCompanionIsBusy" indeterminate size="20" width="2" color="white" />
            <v-icon v-else small color="#ffb74d">mdi-alert-circle</v-icon>
          </button>
        </v-expand-transition>
      </div>

      <v-expand-transition>
        <div
          v-if="isExpanded && !getSelectedExercise"
          class="expanded-content pt-4 px-4 pb-6"
          style="height: 100%; overflow-y: auto; padding-bottom: 80px !important">
          <!-- 1. DASHBOARD HEADER & PROGRESS -->
          <div class="d-flex align-center justify-space-between mb-2">
            <div>
              <div class="text-h5 font-weight-black white--text" style="line-height: 1.1">
                {{ motivationalText }}
              </div>
            </div>
            <div class="text-right">
              <span class="text-h4 font-weight-black" :style="{ color: 'var(--split-color)' }">{{ Math.round(workoutProgress) }}%</span>
            </div>
          </div>
          <v-progress-linear
            :value="workoutProgress"
            height="8"
            rounded
            class="mb-6 elevation-2 header-progress-bar"
            background-color="rgba(255, 255, 255, 0.05)"></v-progress-linear>

          <!-- 2. METRICS GRID -->
          <v-row no-gutters class="justify-space-between mb-4">
            <v-col cols="4" class="pr-1">
              <div class="glass-card grid-card-kcal pa-3 text-center fill-height d-flex flex-column justify-center">
                <v-icon color="#ff7800" class="mb-2">mdi-fire</v-icon>
                <div class="text-h6 font-weight-black white--text">{{ caloriesBurned }}</div>
                <div class="caption grey--text text--lighten-1 font-weight-bold">KCAL</div>
              </div>
            </v-col>
            <v-col cols="4" class="px-1">
              <div class="glass-card grid-card-min pa-3 text-center fill-height d-flex flex-column justify-center">
                <v-icon color="#00b4ff" class="mb-2">mdi-clock-outline</v-icon>
                <div class="text-h6 font-weight-black white--text">{{ workoutMinutes }}</div>
                <div class="caption grey--text text--lighten-1 font-weight-bold">MIN</div>
              </div>
            </v-col>
            <v-col cols="4" class="pl-1">
              <div class="glass-card grid-card-kg pa-3 text-center fill-height d-flex flex-column justify-center">
                <v-icon color="#00e678" class="mb-2">mdi-weight-lifter</v-icon>
                <div class="text-h6 font-weight-black white--text">{{ liftedWeight }}</div>
                <div class="caption grey--text text--lighten-1 font-weight-bold">KG</div>
              </div>
            </v-col>
          </v-row>

          <!-- 3. NEXT UP CARD (VISUAL) -->
          <div v-if="nextExercise" class="glass-card next-up-card mb-4" style="overflow: hidden; position: relative; height: 120px">
            <v-img
              :src="getImageForExercise(nextExercise)"
              height="100%"
              width="100%"
              class="align-center"
              gradient="to right, rgba(18, 18, 18, 0.95) 20%, rgba(18, 18, 18, 0.4) 100%">
              <div class="d-flex align-center pa-4 fill-height">
                <div class="mr-3">
                  <v-sheet :style="{ backgroundColor: 'var(--split-color)' }" width="4" height="40" rounded></v-sheet>
                </div>
                <div style="flex: 1; overflow: hidden">
                  <div class="overline font-weight-bold mb-0" :style="{ color: 'var(--split-color)', letterSpacing: '2px' }">NEXT UP</div>
                  <div class="text-h6 font-weight-bold white--text text-truncate" style="line-height: 1.2">
                    {{ nextExercise.title }}
                  </div>
                  <div class="caption grey--text text--lighten-1 mt-1">
                    {{ nextExercise.sets ? nextExercise.sets.length : 0 }} Sätze geplant
                    <span v-if="nextExerciseHistory" class="font-weight-bold ml-1" :style="{ color: 'var(--split-color)' }">
                      • Letzte: {{ nextExerciseHistory }}
                    </span>
                  </div>
                </div>
                <div class="pl-2">
                  <v-btn
                    icon
                    large
                    class="pulse-arrow"
                    color="white"
                    @click="
                      expandedFix = false
                      divHeight = initialHeight
                    ">
                    <v-icon>mdi-chevron-up</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-img>
          </div>
          <div v-else class="glass-card pa-4 mb-4 d-flex align-center justify-center green darken-4">
            <v-icon left color="white">mdi-check-circle-outline</v-icon>
            <span class="font-weight-bold white--text">Training fast geschafft!</span>
          </div>

          <!-- 4. INSIGHTS & CHART ZONE -->
          <div v-show="overWrittenDivHeight > 300" class="mt-2">
            <v-row>
              <v-col cols="12">
                <!-- RADAR CHART -->
                <div class="glass-card pa-1" style="background: rgba(255, 255, 255, 0.03)">
                  <div class="d-flex align-center px-3 pt-2">
                    <v-icon small color="grey" class="mr-2">mdi-target</v-icon>
                    <span class="caption font-weight-bold grey--text text-uppercase">Muscle Focus</span>
                  </div>
                  <apexchart width="100%" height="180" type="radar" :options="chartOptions" :series="chartSeries"></apexchart>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-expand-transition>

      <v-col
        v-if="!getSelectedExercise && !activeSplit.isHyrox"
        cols="12"
        class="drag-handle pa-0"
        style="position: absolute; bottom: 0px; left: 0; right: 0; z-index: 1001"
        @click="
          () => {
            expandedFix = !expandedFix
            divHeight = divHeight === maxHeight ? initialHeight : maxHeight
          }
        ">
        <div class="drag-handle-container">
          <div class="drag-handle-pill"></div>
        </div>
      </v-col>
    </div>

    <v-card class="fill-height workout-content-area" style="background: #1e1e1e">
      <run-workout-exercise-list
        :initialHeight="initialHeight"
        @open-add-dialog="handleOpenAddDialog"
        @edit="handleEditExercise"
        @move="handleMoveExercise"
        @delete="handleDeleteExercise" />
    </v-card>

    <ExerciseSelectionDialog
      :dialog.sync="selectionDialogActive"
      :mode="selectionDialogMode"
      :insertion-index="selectionDialogInsertionIndex"
      :exercise-to-swap="getSelectedExercise"
      :recommended-tags="swapRecommendedTags"
      @swap-selected="configureNewExercise($event.newExercise)"
      @add-selected="configureNewExercise($event.newExercise)" />
    <help-dialog :dialog.sync="isHelpDialogVisible" />

    <v-dialog v-model="configureDialogVisible" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card dark class="configure-dialog-card d-flex flex-column">
        <div class="configure-dialog-bg-glow"></div>
        <v-card-text class="pa-4 flex-grow-1 overflow-y-auto" style="position: relative; z-index: 1">
          <workout-split-edit-exercise-single
            v-if="exerciseToConfigure"
            :exercise-prop="exerciseToConfigure"
            @savedExercise="finalizeAction"
            @cancel-edit="cancelConfiguration" />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showHyroxStartDialog" fullscreen hide-overlay persistent transition="fade-transition">
      <div class="hyrox-start-overlay" :class="{ 'start-transition': raceClockStarting }">
        <div class="race-clock-placeholder">
          <div class="race-clock-display">{{ raceClockDisplay }}</div>
        </div>

        <div class="hyrox-content">
          <h1 class="hyrox-main-title">
            <span class="char">H</span><span class="char">Y</span><span class="char">R</span><span class="char">O</span><span class="char">X</span>
          </h1>
          <p class="hyrox-subtitle">THE RACE IS ON</p>
          <v-btn x-large rounded depressed color="white" class="font-weight-bold black--text mt-8 start-btn" @click="startTheRace">
            <v-icon left>mdi-timer-play-outline</v-icon>
            START
          </v-btn>
        </div>
      </div>
    </v-dialog>
    <WorkoutTimer />
  </v-dialog>
</template>

<script>
import PlanRunInteractiveHuman from "../../PlanRunInteractiveHuman.vue"
import RunWorkoutExerciseList from "./runWorkoutExerciseList.vue"
// NEU: Importiere die Komponente zum Bearbeiten der Sätze
import WorkoutSplitEditExerciseSingle from "../../WorkoutSplitEditExerciseSingle.vue" // BITTE PFAD PRÜFEN & ANPASSEN
import ExerciseSelectionDialog from "./runWorkoutExerciseSelectionDialog.vue" // Pfad ggf. anpassen
import HelpDialog from "./helpDialog.vue" // Pfad ggf. anpassen
import WorkoutTimer from "./WorkoutTimer.vue" // NEU: Importiere den Timer
import userService from "@/services/userService"
import watchSyncService from "@/services/watchSyncService"
import trainingLiveService from "@/services/trainingLiveService"
import { watchDeviceLabel } from "@/services/watchPlatform"

import { mapState, mapActions, mapGetters } from "vuex"

function getLuminance(hexColor) {
  hexColor = hexColor.replace("#", "")
  const r = parseInt(hexColor.substring(0, 2), 16)
  const g = parseInt(hexColor.substring(2, 4), 16)
  const b = parseInt(hexColor.substring(4, 6), 16)
  return 0.299 * r + 0.587 * g + 0.114 * b
}

export default {
  name: "Workingout",
  components: {
    HelpDialog, // HIER HINZUFÜGEN
    PlanRunInteractiveHuman,
    RunWorkoutExerciseList,
    ExerciseSelectionDialog,
    WorkoutSplitEditExerciseSingle, // NEU
    WorkoutTimer,
  },
  data() {
    return {
      raceClockStartTime: null, // NEU: Für präzise Zeitmessung
      raceClockMilliseconds: 0, // NEU: Wir messen in Millisekunden
      raceClockInterval: null,
      raceClockSeconds: 0,
      raceClockStarted: false, // Steuert die Sichtbarkeit der finalen Uhr
      raceClockStarting: false,
      showHyroxStartDialog: false, // NEU
      showFreeTrainingWelcome: false, // NEU

      isHelpDialogVisible: false, // HIER HINZUFÜGEN

      swapRecommendedTags: [], // NEU: Hält die empfohlenen Tags für den Austausch
      // Umbenannt für Klarheit
      selectionDialogActive: false,
      configureDialogVisible: false,

      // NEU: Steuert den Modus des Dialogs
      selectionDialogMode: "swap",
      selectionDialogInsertionIndex: null,
      configureDialogVisible: false, // NEU: Steuert den Konfigurations-Dialog
      exerciseToConfigure: null, // NEU: Hält die ausgewählte, aber noch nicht konfigurierte Übung
      dialog: true,
      initialHeight: 120,
      expandedThreshold: 180,
      maxHeight: window.innerHeight * 0.9,
      divHeight: 120,
      startY: 0,
      dragging: false,
      timerId: null,
      now: this.$moment(),
      expandedFix: false,
      forceLeaveRoute: false,
    }
  },
  computed: {
    canUseWatchFeature() {
      return userService.hasRight("stat_admin")
    },
    ...mapState("runWorkoutStore", [
      "workoutStartTime",
      "activeSplit",
      "selectedExerciseIndex",
      "completedSetsMap",
      "activeSupersetSubIndex",
      "hubGroupedExercises", // NEU: Zugriff auf History
      "activePlanID",
      "watchCompanion",
    ]),
    ...mapGetters("runWorkoutStore", ["getSelectedExercise", "getActiveSupersetSubExercise"]),
    // ... alle anderen computed properties bleiben unverändert
    isResumingWorkout() {
      return Object.values(this.completedSetsMap).some((sets) => sets.length > 0)
    },
    raceClockDisplay() {
      const totalSeconds = this.raceClockMilliseconds / 1000
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = Math.floor(totalSeconds % 60)
      const hundredths = Math.floor((this.raceClockMilliseconds % 1000) / 10) // Berechnet die Hundertstel

      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}:${String(hundredths).padStart(2, "0")}`
    },
    supersetIndicator() {
      if (this.getSelectedExercise?.isSuperset) {
        const currentIndex = this.activeSupersetSubIndex + 1
        const total = this.getSelectedExercise.exercises.length
        return `Supersatz (${currentIndex}/${total})`
      }
      return ""
    },
    overWrittenDivHeight() {
      const companionHeight = this.canUseWatchFeature && this.watchCompanion.status !== "idle" ? 48 : 0
      if (this.getSelectedExercise) return 60 + companionHeight
      return Math.max(this.initialHeight, this.divHeight) + companionHeight
    },
    watchCompanionIsLive() {
      return ["running", "paused"].includes(this.watchCompanion.status)
    },
    watchCompanionAvailable() {
      return this.canUseWatchFeature && (this.watchCompanion.status !== "idle" || watchSyncService.isAvailable())
    },
    watchCompanionIsBusy() {
      return ["starting", "waiting_for_sensor", "open_watch", "stopping", "discarding"].includes(
        this.watchCompanion.status,
      )
    },
    watchCompanionPulseText() {
      const value = Number(this.watchCompanion.currentHeartRate)
      return Number.isFinite(value) && value > 0 ? Math.round(value) : "--"
    },
    watchCompanionButtonColor() {
      if (this.watchCompanion.status === "error") return "orange darken-2"
      return this.watchCompanionIsLive ? "red darken-2" : "rgba(255,255,255,0.16)"
    },
    watchCompanionStatusTitle() {
      const device = watchDeviceLabel(null, this.watchCompanion.platform)
      const labels = {
        starting: "Watch wird verbunden",
        waiting_for_sensor: "Pulssensor startet",
        open_watch: "Galaxy Watch öffnen",
        running: `${device} · Live`,
        paused: "Watch-Messung pausiert",
        stopping: "Messung wird beendet",
        ended: "Watch-Messung gespeichert",
        busy: "Anderes Watch-Training läuft",
        user_mismatch: "Watch-Benutzer prüfen",
        error: "Watch nicht verbunden",
      }
      return labels[this.watchCompanion.status] || device
    },
    watchCompanionStatusDetail() {
      if (this.watchCompanion.error) return this.watchCompanion.error
      if (this.watchCompanionIsLive) {
        const zone = Number(this.watchCompanion.heartRateZone)
        const zoneText = zone >= 1 && zone <= 5 ? `Zone ${zone}` : "Puls wird erfasst"
        const calories = Math.round(Number(this.watchCompanion.activeEnergyKilocalories) || 0)
        return `${zoneText} · ${calories} aktive kcal`
      }
      return "Roody auf der Watch öffnen, falls nötig"
    },
    currentExerciseDisplayTitle() {
      if (!this.getSelectedExercise) {
        return this.activeSplit.title
      }
      if (this.getSelectedExercise.isSuperset) {
        // Wenn Supersatz, nimm den Titel der aktiven Sub-Übung
        return this.getActiveSupersetSubExercise?.title || "Supersatz"
      }
      // Ansonsten den normalen Titel
      return this.getSelectedExercise.title
    },
    workoutMinutes() {
      if (!this.workoutStartTime) return 0
      return this.now.diff(this.workoutStartTime, "minutes")
    },
    isExpanded() {
      return this.expandedFix
      return !this.getSelectedExercise && this.divHeight > this.expandedThreshold
    },
    mappedExercises() {
      if (!this.activeSplit || !this.activeSplit.exercises) return []
      return this.activeSplit.exercises.map((e) => ({
        title: e.title,
        device: e.deviceTitle,
        tags: e.tags,
        img: this.getImageForExercise(e),
      }))
    },
    activeClasses() {
      if (!this.activeSplit || !this.activeSplit.exercises) return []
      return this.activeSplit.exercises.reduce((acc, e) => {
        e.tags.forEach((t) => {
          if (!acc.includes(t.class)) acc.push(t.class)
        })
        return acc
      }, [])
    },
    caloriesBurned() {
      return Math.round(this.workoutMinutes * 7 * 10) / 10
    },
    // KORRIGIERT: Greift jetzt auf completedSetsMap zu.
    liftedWeight() {
      if (!this.completedSetsMap) return 0
      const allSets = Object.values(this.completedSetsMap).flat()
      const totalWeight = allSets.reduce((total, set) => {
        if (!Array.isArray(set) || set.some((s) => s.skipped)) {
          return total
        }
        const weight = set.find((item) => item.text === "Gewicht")?.value || 0
        const reps = set.find((item) => /Wiederholungen/.test(item.text))?.value || 0
        return total + Number(weight) * Number(reps)
      }, 0)
      return Math.round(totalWeight)
    },
    expandedMetrics() {
      return [
        { title: "Kalorien", value: this.caloriesBurned, unit: "kcal", icon: "mdi-fire", iconColor: "orange" },
        { title: "Zeit", value: this.workoutMinutes, unit: "Min", icon: "mdi-timer-sand", iconColor: "blue" },
        { title: "Gewicht", value: this.liftedWeight, unit: "kg", icon: "mdi-weight-lifter", iconColor: "green" },
      ]
    },
    currentBackgroundColorHex() {
      if (this.getSelectedExercise?.warmup && !this.getSelectedExercise?.sets?.length) return "#FFA500"
      if (this.getSelectedExercise) return "#292929"
      if (this.activeSplit?.fixedVisualization?.color) return this.activeSplit.fixedVisualization.color
      return "#1e1e1e" // Fallback
    },
    splitColorRgb() {
      const hex = this.currentBackgroundColorHex
      const { r, g, b } = this.hexToRgb(hex)
      return `${r}, ${g}, ${b}`
    },
    // --- NEUE COMPUTED PROPERTIES FÜR EXPANDED HEADER ---
    totalExercisesCount() {
      return this.activeSplit?.exercises?.length || 0
    },
    completedExercisesCount() {
      if (!this.activeSplit || !this.completedSetsMap) return 0
      return this.activeSplit.exercises.reduce((count, ex, index) => {
        const sets = this.completedSetsMap[index] || []
        // Vereinfachte Logik: Wenn Anzahl Sets >= Geplante Sets (ohne Warmup)
        // Bei Supersätzen nehmen wir einfach an, sie sind "Teil" des Ganzen.
        // Genauere Logik wäre komplex, dies reicht für die Progress-Bar.
        const planned = ex.sets ? ex.sets.length : 0
        const completed = sets.filter((s) => !s.skipped && !s.isWarmup).length
        if (planned > 0 && completed >= planned) return count + 1
        return count
      }, 0)
    },
    workoutProgress() {
      if (this.totalExercisesCount === 0) return 0
      return (this.completedExercisesCount / this.totalExercisesCount) * 100
    },
    nextExercise() {
      if (!this.activeSplit) return null
      const firstIncompleteIndex = this.activeSplit.exercises.findIndex((ex, index) => {
        const sets = this.completedSetsMap[index] || []
        const planned = ex.sets ? ex.sets.length : 0
        const completed = sets.filter((s) => !s.skipped && !s.isWarmup).length
        return completed < planned
      })

      if (firstIncompleteIndex !== -1) {
        return this.activeSplit.exercises[firstIncompleteIndex]
      }
      return null
    },
    // NEU: Holt historische Daten für die nächste Übung
    nextExerciseHistory() {
      if (!this.nextExercise || !this.hubGroupedExercises) return null
      // Finde Eintrag in history
      const historyEntry = this.hubGroupedExercises.find((h) => {
        const hId = h.exercise && h.exercise._id ? h.exercise._id : h.exercise
        const nId = this.nextExercise.exerciseId || (this.nextExercise.exercise && this.nextExercise.exercise._id)
        return hId === nId
      })

      if (historyEntry && historyEntry.summary && historyEntry.summary.maxWeightStep) {
        return `${historyEntry.summary.maxWeightStep.weight}kg`
      }
      return null
    },
    // NEU: Dynamischer Motivationstext
    motivationalText() {
      const p = this.workoutProgress
      if (p === 0) return "Let's Go!"
      if (p < 25) return "Aufwärmen..."
      if (p < 50) return "Keep Pushing!"
      if (p < 75) return "Strong Work!"
      if (p < 90) return "Almost There!"
      if (p < 100) return "Final Push!"
      return "Done!"
    },
    // KORRIGIERT & ERWEITERT: Robuste Tag-Logik + Chart Daten
    workoutTags() {
      if (!this.activeSplit || !this.activeSplit.exercises) return []
      const allTags = new Set()
      this.activeSplit.exercises.forEach((ex) => {
        if (ex.tags) {
          ex.tags.forEach((t) => {
            // Prüfen ob String oder Objekt
            const label = typeof t === "object" ? t.title || t.text : t
            if (label) allTags.add(label)
          })
        }
      })
      return Array.from(allTags).slice(0, 3)
    },
    heaviestLiftToday() {
      if (!this.completedSetsMap) return 0
      let maxWeight = 0
      Object.values(this.completedSetsMap)
        .flat()
        .forEach((set) => {
          if (set && !set.skipped) {
            const w = parseFloat(set.find((i) => i.text === "Gewicht")?.value || 0)
            if (w > maxWeight) maxWeight = w
          }
        })
      return maxWeight
    },
    totalRepsToday() {
      if (!this.completedSetsMap) return 0
      let total = 0
      Object.values(this.completedSetsMap)
        .flat()
        .forEach((set) => {
          if (set && !set.skipped) {
            const r = parseFloat(set.find((i) => i.text === "Wiederholungen" || i.text === "Min Wiederholungen")?.value || 0)
            if (!isNaN(r)) total += r
          }
        })
      return total
    },
    // DATEN FÜR APEXCHART RADAR & INSIGHTS
    categoryDistribution() {
      const categories = { Beine: 0, Push: 0, Pull: 0, Core: 0, Cardio: 0 }

      if (this.activeSplit && this.activeSplit.exercises) {
        this.activeSplit.exercises.forEach((ex) => {
          if (ex.tags) {
            ex.tags.forEach((t) => {
              // Wir nutzen die 'class' Eigenschaft aus deinen Tags für Robustheit
              const tagKey = (typeof t === "object" ? t.class || t.name : t).toLowerCase()

              // MAPPING LOGIK
              if (
                [
                  "oberschenkel",
                  "wade",
                  "gesaess",
                  "beinbeuger",
                  "schienbein",
                  "scholle",
                  "adduktoren",
                  "abduktoren",
                  "quadriceps",
                  "hamstrings",
                  "calves",
                  "glutes",
                ].some((k) => tagKey.includes(k))
              ) {
                categories["Beine"] += 1
              } else if (
                ["brust", "trizeps", "vordere-schulter", "seitliche-schulter", "chest", "triceps", "front-delt", "side-delt", "push"].some((k) =>
                  tagKey.includes(k),
                )
              ) {
                categories["Push"] += 1
              } else if (
                [
                  "latissimus",
                  "bizeps",
                  "trapez",
                  "hintere-schulter",
                  "ruecken-strecker",
                  "nacken",
                  "unterarm",
                  "back",
                  "biceps",
                  "rear-delt",
                  "pull",
                ].some((k) => tagKey.includes(k))
              ) {
                categories["Pull"] += 1
              } else if (["bauch", "gerader-bauch", "seitlicher-bauch", "abs", "core"].some((k) => tagKey.includes(k))) {
                categories["Core"] += 1
              } else if (["herz-kreislauf", "cardio", "running", "rowing"].some((k) => tagKey.includes(k))) {
                categories["Cardio"] += 1
              }
            })
          }
        })
      }
      return categories
    },
    chartSeries() {
      return [
        {
          name: "Intensität",
          data: Object.values(this.categoryDistribution),
        },
      ]
    },
    sessionInsights() {
      const insights = []

      // 1. Volume Badge
      if (this.liftedWeight > 10000) insights.push({ icon: "mdi-weight", color: "red", title: "IRON MOVER", text: "> 10 Tonnen" })
      else if (this.liftedWeight > 5000) insights.push({ icon: "mdi-dumbbell", color: "orange", title: "HEAVY LIFTER", text: "> 5 Tonnen" })

      // 2. Intensity / Focus Badge
      const dist = this.categoryDistribution
      const maxCat = Object.keys(dist).reduce((a, b) => (dist[a] > dist[b] ? a : b))

      if (dist[maxCat] > 0) {
        if (maxCat === "Beine") insights.push({ icon: "mdi-run", color: "blue", title: "LEG DAY", text: "Bein-Fokus" })
        if (maxCat === "Push") insights.push({ icon: "mdi-arm-flex", color: "green", title: "PUSH POWER", text: "Druck-Fokus" })
        if (maxCat === "Pull") insights.push({ icon: "mdi-rowing", color: "purple", title: "PULL MASTER", text: "Zug-Fokus" })
        if (maxCat === "Cardio") insights.push({ icon: "mdi-heart-pulse", color: "red", title: "CARDIO ENGINE", text: "Ausdauer" })
      }

      // 3. Repetition Badge
      if (this.totalRepsToday > 200) insights.push({ icon: "mdi-counter", color: "cyan", title: "HIGH VOLUME", text: "200+ Reps" })

      // Fallback
      if (insights.length === 0) insights.push({ icon: "mdi-fire", color: "orange", title: "ON FIRE", text: "Training läuft" })

      return insights
    },
    chartOptions() {
      const isDark = this.isBackgroundDark()
      const textColor = isDark ? "#fff" : "#000"
      const gridColor = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"
      const strokeColor = isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)"

      return {
        chart: {
          type: "radar",
          toolbar: { show: false },
          parentHeightOffset: 0,
          fontFamily: "Roboto, sans-serif",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150,
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350,
            },
          },
        },
        labels: ["Beine", "Push", "Pull", "Core", "Cardio"],
        stroke: { width: 2, colors: [strokeColor], dashArray: 0 },
        fill: { opacity: 0.3, colors: [textColor] },
        markers: { size: 3, colors: [textColor], strokeWidth: 0 },
        yaxis: { show: false, min: 0 },
        xaxis: {
          labels: {
            style: {
              colors: [textColor, textColor, textColor, textColor, textColor],
              fontSize: "10px",
              fontWeight: 700,
              fontFamily: "Roboto",
            },
          },
        },
        plotOptions: {
          radar: {
            size: 60,
            polygons: {
              strokeColors: gridColor,
              connectorColors: gridColor,
            },
          },
        },
        tooltip: { enabled: false },
        grid: { padding: { top: 0, bottom: -10 } },
      }
    },
  },
  methods: {
    ...mapActions("runWorkoutStore", [
      "resetWorkoutStartTime",
      "setSelectedExerciseIndex",
      "swapExerciseTemporarily",
      "addExerciseTemporarily",
      "updateExerciseInSplit", // NEU
      "moveExercise", // NEU
      "removeExerciseTemporarily",
    ]),
    async startWatchPulseTracking() {
      if (!this.canUseWatchFeature) return
      if (this.watchCompanionIsLive) {
        this.$toast.success(`Watch-Pulstracking läuft mit ${this.watchCompanionPulseText} BPM`)
        return
      }
      if (this.watchCompanionIsBusy) return

      try {
        await watchSyncService.startPhoneCompanionWorkout({
          clientSessionId: this.watchCompanion.clientSessionId,
          title: this.activeSplit?.title || "Training",
          workoutId: this.activePlanID || this.activeSplit?.planID || null,
          splitKey: this.activeSplit?._id || this.activeSplit?.title || null,
          planRevision: this.activeSplit?.updatedAt || null,
          startedAt: this.workoutStartTime,
          healthActivityType: this.activeSplit?.isHyrox
            ? "crossTraining"
            : "traditionalStrengthTraining",
        })
        this.$toast.success(`Pulstracking auf der ${watchDeviceLabel(null, this.watchCompanion.platform)} gestartet`)
      } catch (error) {
        this.$toast.error(error?.message || "Watch konnte nicht gestartet werden")
      }
    },
    startTheRace() {
      this.raceClockStarting = true // Startet die Übergangs-Animation
      // Wir geben der Animation etwas mehr Zeit, um sauber auszusehen
      setTimeout(() => {
        this.showHyroxStartDialog = false
        this.raceClockStarted = true
        this.startRaceClock()
      }, 600) // Dauer auf 1.2s erhöht
    },
    startRaceClock() {
      if (this.raceClockInterval) clearInterval(this.raceClockInterval)

      // Die Uhr basiert jetzt IMMER auf der ursprünglichen Startzeit aus dem Store
      const initialStartTime = this.$moment(this.workoutStartTime).add("seconds", 3)
      const updateClock = () => {
        const now = this.$moment()
        const elapsed = now.diff(initialStartTime) // Differenz in Millisekunden
        this.raceClockMilliseconds = Math.max(0, elapsed)
      }

      updateClock() // Einmal sofort ausführen, um die korrekte Zeit sofort anzuzeigen
      this.raceClockInterval = setInterval(updateClock, 51) // Hochfrequentes Update für flüssige Hundertstel
    },
    openAddExerciseFromWelcome() {
      this.showFreeTrainingWelcome = false
      this.handleOpenAddDialog()
    },
    // NEU: Editieren einer Übung
    handleEditExercise(exercise) {
      // Wir nutzen denselben Flow wie beim Adden, aber mit Initialdaten
      this.selectionDialogMode = "edit"
      // Finde den Index
      const idx = this.activeSplit.exercises.findIndex((e) => e._id === exercise._id)
      if (idx === -1) return

      this.setSelectedExerciseIndex(idx) // KORREKT: Über den Store gehen
      this.exerciseToConfigure = JSON.parse(JSON.stringify(exercise)) // Deep Copy
      this.configureDialogVisible = true
    },
    // NEU: Verschieben einer Übung
    handleMoveExercise({ exercise, direction }) {
      const idx = this.activeSplit.exercises.findIndex((e) => e._id === exercise._id)
      if (idx === -1) return

      const newIndex = direction === "up" ? idx - 1 : idx + 1
      if (newIndex < 0 || newIndex >= this.activeSplit.exercises.length) return

      this.moveExercise({ fromIndex: idx, toIndex: newIndex })
    },
    // NEU: Entfernen einer Übung
    handleDeleteExercise(exercise) {
      if (confirm(`Möchtest du die Übung "${exercise.title}" wirklich aus dem Trainingsplan entfernen?`)) {
        const idx = this.activeSplit.exercises.findIndex((e) => e._id === exercise._id)
        if (idx === -1) return
        this.removeExerciseTemporarily(idx)
      }
    },
    updateLiveActivity() {
      trainingLiveService.schedule()
    },
    getNativeAssetName(imagePath) {
      if (!imagePath) return "default_split_image" // Fallback-Bildname
      // Extrahiert 'mein_bild' aus 'pfad/zu/mein_bild.png'
      return imagePath.split("/").pop().split(".")[0]
    },
    // --- NEUE UND ANGEPASSTE METHODEN FÜR DEN SWAP-PROZESS ---
    openSwapDialog() {
      if (!this.getSelectedExercise) return
      const currentExerciseInPlan = this.activeSplit.exercises[this.selectedExerciseIndex]
      this.swapRecommendedTags = Array.isArray(currentExerciseInPlan.recommendedTags) ? currentExerciseInPlan.recommendedTags : []

      this.selectionDialogMode = "swap"
      this.selectionDialogActive = true
    },

    // Wird vom Kind-Component per Event aufgerufen
    handleOpenAddDialog() {
      this.selectionDialogMode = "add"
      this.selectionDialogInsertionIndex = this.activeSplit.exercises.length // Füge am Ende des Plans hinzu
      this.swapRecommendedTags = [] // Keine Empfehlungen beim Hinzufügen
      this.selectionDialogActive = true
    },

    configureNewExercise(newExercise) {
      this.selectionDialogActive = false
      this.exerciseToConfigure = JSON.parse(JSON.stringify(newExercise))
      this.configureDialogVisible = true
    },

    // Diese Methode schliesst den gesamten Vorgang ab
    finalizeAction(configuredExercise) {
      if (this.selectionDialogMode === "swap") {
        if (this.selectedExerciseIndex === null) return
        this.swapExerciseTemporarily({
          exerciseIndex: this.selectedExerciseIndex,
          newExercise: configuredExercise,
        })
        this.$toast.success(`${configuredExercise.title} wurde erfolgreich ausgetauscht!`)
      } else if (this.selectionDialogMode === "edit") {
        // NEU: Edit Mode
        if (this.selectedExerciseIndex === null) return
        this.updateExerciseInSplit({
          exerciseIndex: this.selectedExerciseIndex,
          newExercise: configuredExercise,
        })
        this.$toast.success(`${configuredExercise.title} wurde aktualisiert!`)
      } else {
        // mode === 'add'
        this.addExerciseTemporarily({
          newExercise: configuredExercise,
          atIndex: this.selectionDialogInsertionIndex,
        })
        this.$toast.success(`${configuredExercise.title} wurde dem Plan hinzugefügt!`)
      }
      this.cancelConfiguration()
    },

    cancelConfiguration() {
      this.configureDialogVisible = false
      this.exerciseToConfigure = null
      this.selectionDialogInsertionIndex = null
    },
    getHeaderBackground() {
      if (this.activeSplit?.isHyrox) {
        return "rgba(20, 20, 20, 0.85)"
      }
      if (!this.activeSplit) return "rgba(30, 30, 30, 0.7)"
      if (this.getSelectedExercise?.warmup && !this.getSelectedExercise?.sets?.length) return "rgba(255, 165, 0, 0.75)"
      if (this.getSelectedExercise) return "rgba(41, 41, 41, 0.8)"

      const hex = this.activeSplit.fixedVisualization.color || "#1e1e1e"
      const { r, g, b } = this.hexToRgb(hex)
      return `rgba(${r}, ${g}, ${b}, 0.65)`
    },
    isBackgroundDark(hexColor = this.currentBackgroundColorHex) {
      if (this.activeSplit?.isHyrox) {
        return true
      }
      const luminance = getLuminance(hexColor)
      return this.getSelectedExercise?.warmup || luminance < 128
    },
    getTextColorBasedOnBackground() {
      return this.isBackgroundDark() ? "white" : "black"
    },
    getTextColorBasedOnBackgroundClass() {
      return this.isBackgroundDark() ? "white--text" : "black--text"
    },
    getDragHandleColor() {
      return this.isBackgroundDark() ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.3)"
    },
    getInteractiveHumanBackground() {
      return this.isBackgroundDark() ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
    },
    updateHeaderColorForFlutter() {
      const { r, g, b } = this.hexToRgb(this.currentBackgroundColorHex)
      let rgbString = `rgb(${r}, ${g}, ${b})`
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler("colorHandler", rgbString)
      }
    },
    async onClose() {
      if (this.getSelectedExercise) {
        this.setSelectedExerciseIndex(-1)
        this.forceLeaveRoute = false
      } else {
        this.dialog = false
        this.forceLeaveRoute = true
        this.$router.replace({ name: "Workouts_v2" }) // Ziel anpassen falls nötig
      }
    },
    startDragging(event) {
      this.startY = event.touches[0].clientY
      this.dragging = true
      window.addEventListener("touchmove", this.drag, { passive: false })
      window.addEventListener("touchend", this.stopDragging)
    },
    drag(event) {
      if (!this.dragging) return
      event.preventDefault()
      const touchY = event.touches[0].clientY
      const deltaY = this.startY - touchY
      this.startY = touchY
      let newHeight = this.divHeight - deltaY
      this.divHeight = Math.max(this.initialHeight, Math.min(this.maxHeight, newHeight))
    },
    stopDragging() {
      this.dragging = false
      window.removeEventListener("touchmove", this.drag)
      window.removeEventListener("touchend", this.stopDragging)
      if (!this.getSelectedExercise) {
        if (this.divHeight < (this.maxHeight + this.initialHeight) / 2) {
          this.divHeight = this.initialHeight
        } else {
          this.divHeight = this.maxHeight
        }
      }
    },
    hexToRgb(hex) {
      hex = hex.replace(/^#/, "")
      if (hex.length === 3)
        hex = hex
          .split("")
          .map((ch) => ch + ch)
          .join("")
      if (hex.length !== 6) return { r: 0, g: 0, b: 0 }
      const bigint = parseInt(hex, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = bigint & 255
      return { r, g, b }
    },
    getImageForExercise(exercise) {
      if (exercise.picture) return this.getImageUrl(exercise.picture)
      if (exercise.manual && exercise.manual[0]?.picture) return this.getImageUrl(exercise.manual[0].picture)
      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },
    getImageUrl(imageId) {
      if (!imageId) return ""
      return `/img/uebungen/${imageId.replace(/\.[^.]+$/, "")}.webp`
    },
  },
  created() {
    this.updateHeaderColorForFlutter()
    if (!this.workoutStartTime || this.$moment().diff(this.workoutStartTime, "hours") > 5) {
      this.resetWorkoutStartTime()
    }
    this.divHeight = this.initialHeight
  },
  // In Workingout.vue -> mounted()
  async mounted() {
    this.$store.dispatch("runWorkoutStore/fetchGlobalWeightStatistics")

    this.timerId = setInterval(() => {
      this.now = this.$moment()
      this.updateLiveActivity()
    }, 15000) // Update alle 15 Sekunden ist ein guter Kompromiss

    // --- START: NEUE LOGIK ---
    if (this.activeSplit) {
      const titleLower = this.activeSplit.title.toLowerCase()
      if (titleLower.includes("hyrox")) {
        // Speziell für Hyrox den neuen Dialog anzeigen
        if (this.isResumingWorkout) {
          this.raceClockStarted = true
          this.startRaceClock()
        } else {
          this.showHyroxStartDialog = true
        }
      } else if (this.activeSplit.isFreeTraining) {
        // Den alten Dialog nur noch für NICHT-Hyrox freie Trainings anzeigen
        this.showFreeTrainingWelcome = true
      }
    }

    this.maxHeight = window.innerHeight * 0.75

    trainingLiveService.resume()
    trainingLiveService.schedule()
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === "WorkoutSummary") {
      next()
      return
    }
    if (this.forceLeaveRoute) {
      next()
      return
    }
    this.onClose()
    next(false)
  },
  beforeDestroy() {
    clearInterval(this.timerId)
    clearInterval(this.raceClockInterval) // NEU
    window.removeEventListener("touchmove", this.drag)
    window.removeEventListener("touchend", this.stopDragging)
  },
  watch: {
    getSelectedExercise(newValue) {
      this.updateHeaderColorForFlutter()
      this.divHeight = newValue ? 60 : this.initialHeight
    },
    currentBackgroundColorHex() {
      this.updateHeaderColorForFlutter()
    },
  },
}
</script>
<style lang="scss" scoped>
.configure-dialog-card {
  background-color: #121212 !important;
  color: white;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.configure-dialog-bg-glow {
  position: absolute;
  width: 100%;
  height: 50%;
  top: 0;
  left: 0;
  background: radial-gradient(circle at 50% 0%, rgba(33, 150, 243, 0.1), transparent 75%);
  pointer-events: none;
  z-index: 0;
}

.mainpage-top {
  position: fixed;
  overflow: hidden;
  margin: 0 auto;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: height 0.45s cubic-bezier(0.16, 1, 0.3, 1), border-radius 0.45s ease, background 0.45s ease;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  box-shadow: 0 10px 30px -10px var(--shadow-color) !important;
  border-bottom: 1px solid var(--border-color) !important;
}
.header-row-base {
  height: 50px;
  flex-wrap: nowrap;
}
.collapsed-metrics .metric-item {
  padding: 0 4px;
}
.metric-card-expanded {
  background-color: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.metric-card-expanded:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}
.drag-handle {
  cursor: ns-resize;
}
.workout-content-area {
  background-color: #121212;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  overflow-y: auto;
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.05);
}

/* --- START: VERBESSERTES "WOW-FAKTOR" STYLING --- */

.welcome-overlay {
  background: radial-gradient(circle at center, #2a2a2a, #121212);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding: 24px;
  height: 100vh;
  overflow: hidden;
  /* NEU: Der ganze Hintergrund blendet sanft ein */
  animation: overlay-fade-in 0.5s ease-out forwards;
}

.welcome-content > * {
  /* Stellt sicher, dass alle Kind-Elemente unsichtbar starten */
  opacity: 0;
}

.emoji-line {
  font-size: 5rem;
  text-shadow: 0 0 40px rgba(255, 255, 255, 0.7);
  animation: pop-in 0.8s 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

.welcome-title {
  font-size: 6rem;
  font-weight: 900;
  line-height: 1;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.2), 0 0 40px rgba(255, 255, 255, 0.5);
  letter-spacing: -0.05em;
  /* Wichtig, damit die Kind-Animationen funktionieren */
  animation: fade-in 0.1s 0.6s forwards;
}

.welcome-title span {
  display: inline-block;
  opacity: 0;
  transform: translateY(60px) rotate(8deg);
  /* NEU: Weichere und deutlichere Animation */
  animation: reveal-text 0.8s cubic-bezier(0.19, 1, 0.22, 1) forwards;
}

.welcome-title .line1 {
  animation-delay: 0.8s;
}
.welcome-title .line2 {
  animation-delay: 0.9s;
}

.welcome-subtitle {
  font-size: 1.1rem;
  max-width: 350px;
  margin: 24px auto 0;
  color: #b0b0b0;
  animation: fade-in-up 0.8s 1.2s cubic-bezier(0.19, 1, 0.22, 1) forwards;
}

.start-btn {
  animation: fade-in-up 0.8s 1.4s cubic-bezier(0.19, 1, 0.22, 1) forwards, pulse-shadow 2.5s 2s infinite ease-in-out;
  box-shadow: 0 0 25px rgba(var(--v-primary-base-rgb), 0.4);
}

/* --- KEYFRAME ANIMATIONEN FÜR DEN "WOW-FAKTOR" --- */

@keyframes overlay-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pop-in {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes reveal-text {
  from {
    opacity: 0;
    transform: translateY(60px) rotate(8deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) rotate(0deg);
  }
}

@keyframes pulse-shadow {
  0% {
    box-shadow: 0 0 25px rgba(var(--v-primary-base-rgb), 0.4);
  }
  50% {
    box-shadow: 0 0 40px 8px rgba(var(--v-primary-base-rgb), 0.6);
  }
  100% {
    box-shadow: 0 0 25px rgba(var(--v-primary-base-rgb), 0.4);
  }
}

/* In runWorkout.vue -> <style> */

/* --- START: EPISCHES STYLING FÜR HYROX-START --- */

.hyrox-start-overlay {
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.hyrox-start-overlay::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 150%;
  padding-bottom: 150%;
  background: radial-gradient(circle, rgba(255, 237, 0, 0.2) 0%, transparent 50%);
  transform-origin: center;
  transform: translate(-50%, -50%) scale(0);
  animation: background-pulse 2s 0.5s forwards;
}

.hyrox-content {
  position: relative;
  z-index: 2;
}

.hyrox-main-title {
  font-size: 10rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.1em;
  color: #fff;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.5), 0 0 30px #ffed00;
}

.hyrox-main-title .char {
  display: inline-block;
  opacity: 0;
  transform: translateY(-80px) scale(0.5);
  animation: letter-drop 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

/* Staggered animation für die Buchstaben */
.hyrox-main-title .char:nth-child(1) {
  animation-delay: 0.8s;
}
.hyrox-main-title .char:nth-child(2) {
  animation-delay: 0.9s;
}
.hyrox-main-title .char:nth-child(3) {
  animation-delay: 1s;
}
.hyrox-main-title .char:nth-child(4) {
  animation-delay: 1.1s;
}
.hyrox-main-title .char:nth-child(5) {
  animation-delay: 1.2s;
}

.hyrox-subtitle {
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  color: #ffed00;
  text-transform: uppercase;
  margin-top: 1rem;
  opacity: 0;
  animation: fade-in-up 1s 1.5s forwards;
}

.hyrox-content .start-btn {
  opacity: 0;
  animation: fade-in-up 1s 1.8s forwards;
}

@keyframes background-pulse {
  from {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

@keyframes letter-drop {
  from {
    opacity: 0;
    transform: translateY(-80px) scale(0.5) rotate(-45deg);
    filter: blur(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0deg);
    filter: blur(0);
  }
}

.mainpage-top.hyrox-mode-active {
  background: #141414 !important;
  display: flex;
  flex-direction: column;
}
.header-content-wrapper {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  bottom: 8px;
}
.race-clock-container {
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.4s 0.2s ease, transform 0.4s 0.2s ease;
}
.race-clock-container.animate-in {
  opacity: 1;
  transform: translateY(0);
}
.race-clock-display {
  font-family: "Teko", sans-serif;
  font-size: 3.5rem;
  font-weight: 700;
  color: #ffed00;
  text-shadow: 0 0 10px rgba(255, 237, 0, 0.7);
  line-height: 1;
}

/* --- NEUE LOGIK FÜR DEN ÜBERGANG --- */

/* Die Platzhalter-Uhr, die genau dort sitzt, wo die echte Uhr sein wird */
.race-clock-placeholder {
  position: absolute;
  top: 60px; /* Höhe des Titel-Headers */
  left: 0;
  right: 0;
  height: 120px; /* Höhe des gesamten Headers */
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0; /* Startet unsichtbar */
}
.hyrox-start-overlay.start-transition .race-clock-placeholder {
  animation: fade-in 0.5s 0.7s forwards; /* Blendet sich am Ende ein */
}

/* Der Titel wird jetzt einfach kleiner und blendet aus */
.hyrox-start-overlay.start-transition .hyrox-main-title {
  animation: scale-and-fade-out 0.8s cubic-bezier(0.5, 0, 0.2, 1) forwards;
}

/* Das Overlay selbst blendet am Ende aus */
.hyrox-start-overlay.start-transition {
  animation: fade-out-bg 0.4s 0.8s forwards;
}

/* Die anderen Elemente blenden früher aus */
.hyrox-start-overlay.start-transition .hyrox-subtitle,
.hyrox-start-overlay.start-transition .start-btn {
  animation: fade-out-down 0.3s ease-out forwards;
}

/* NEUE KEYFRAMES */
@keyframes pulse-arrow {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

.pulse-arrow {
  animation: pulse-arrow 2s infinite;
}

@keyframes scale-and-fade-out {
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(0.2);
    opacity: 0;
  }
}
@keyframes fade-out-bg {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes fade-out-down {
  to {
    opacity: 0;
    transform: translateY(50px);
  }
}

/* PREMIUM HEADER STYLES */
.premium-header-shadow {
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5) !important;
}

.header-glass-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(-45deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  background-size: 400% 400%;
  pointer-events: none;
  z-index: 1;
}

@keyframes gradientBG {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.glass-btn {
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  border-radius: 50%;
  width: 36px !important;
  height: 36px !important;
}

.glass-btn:hover {
  background: rgba(255, 255, 255, 0.15) !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
  transform: scale(1.08);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25), 0 0 10px rgba(var(--split-color-rgb, 255, 255, 255), 0.2);
}

.glass-btn:active {
  transform: scale(0.95);
}

.header-title {
  font-family: "Outfit", "Inter", -apple-system, sans-serif !important;
  font-size: 1.15rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.metric-capsule {
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 95px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.metric-capsule.light-theme {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

/* Individual capsule styles */
.metric-capsule.capsule-kcal {
  border-color: rgba(255, 120, 0, 0.25);
}
.metric-capsule.capsule-kcal:hover {
  background: rgba(255, 120, 0, 0.1);
  border-color: rgba(255, 120, 0, 0.5);
  box-shadow: 0 4px 15px rgba(255, 120, 0, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.metric-capsule.capsule-min {
  border-color: rgba(0, 180, 255, 0.25);
}
.metric-capsule.capsule-min:hover {
  background: rgba(0, 180, 255, 0.1);
  border-color: rgba(0, 180, 255, 0.5);
  box-shadow: 0 4px 15px rgba(0, 180, 255, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.metric-capsule.capsule-kg {
  border-color: rgba(0, 230, 120, 0.25);
}
.metric-capsule.capsule-kg:hover {
  background: rgba(0, 230, 120, 0.1);
  border-color: rgba(0, 230, 120, 0.5);
  box-shadow: 0 4px 15px rgba(0, 230, 120, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.metric-val {
  font-size: 0.95rem;
  margin-right: 3px;
  letter-spacing: -0.2px;
  font-weight: 800;
}

.metric-unit {
  font-size: 0.7rem;
  opacity: 0.8;
  font-weight: 700;
  text-transform: uppercase;
}

/* EXPANDED DASHBOARD STYLES */
.glass-card {
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.glass-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
}

.glass-card:hover {
  transform: translateY(-4px);
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

/* Specific glows for expanded grid cards */
.grid-card-kcal:hover {
  box-shadow: 0 8px 25px rgba(255, 120, 0, 0.15), 0 0 15px rgba(255, 120, 0, 0.05);
  border-color: rgba(255, 120, 0, 0.3) !important;
}
.grid-card-kcal .v-icon {
  text-shadow: 0 0 10px rgba(255, 120, 0, 0.5);
  filter: drop-shadow(0 0 4px rgba(255, 120, 0, 0.4));
}

.grid-card-min:hover {
  box-shadow: 0 8px 25px rgba(0, 180, 255, 0.15), 0 0 15px rgba(0, 180, 255, 0.05);
  border-color: rgba(0, 180, 255, 0.3) !important;
}
.grid-card-min .v-icon {
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
  filter: drop-shadow(0 0 4px rgba(0, 180, 255, 0.4));
}

.grid-card-kg:hover {
  box-shadow: 0 8px 25px rgba(0, 230, 120, 0.15), 0 0 15px rgba(0, 230, 120, 0.05);
  border-color: rgba(0, 230, 120, 0.3) !important;
}
.grid-card-kg .v-icon {
  text-shadow: 0 0 10px rgba(0, 230, 120, 0.5);
  filter: drop-shadow(0 0 4px rgba(0, 230, 120, 0.4));
}

.next-up-card {
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3) !important;
}

.pulse-arrow {
  background: rgba(255, 255, 255, 0.12) !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  animation: pulse-arrow 2s infinite;
}
.pulse-arrow:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  transform: scale(1.08);
}

.header-progress-bar {
  overflow: visible !important;
  background: rgba(255, 255, 255, 0.06) !important;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.5) !important;
}

.header-progress-bar ::v-deep .v-progress-linear__determinate {
  background: linear-gradient(90deg, var(--split-color), #ffffff) !important;
  box-shadow: 0 0 12px var(--split-color) !important;
  border-radius: 4px;
}

.drag-handle-container {
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent);
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  cursor: ns-resize;
}

.drag-handle-pill {
  height: 4px;
  width: 40px;
  border-radius: 2px;
  background-color: var(--border-color);
  transition: all 0.3s ease;
  opacity: 0.6;
}

.drag-handle:hover .drag-handle-pill {
  opacity: 1;
  width: 50px;
  background-color: var(--split-color, #fff);
  box-shadow: 0 0 8px var(--split-color);
}

.title-font {
  font-size: 1.4rem;
  line-height: 1.2;
}

.caption-font {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 1px;
}

.opacity-70 {
  opacity: 0.7;
}

.filter-glow {
  filter: drop-shadow(0 0 8px currentColor);
}

.transparent-carousel .v-carousel__controls {
  background: transparent !important;
}

.watch-pulse-trigger {
  min-width: 38px !important;
  padding: 0 9px !important;
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  backdrop-filter: blur(12px);
}

.watch-pulse-trigger--live {
  box-shadow: 0 0 0 1px rgba(255, 77, 94, 0.32), 0 0 18px rgba(255, 77, 94, 0.3) !important;
}

.watch-companion-live-strip {
  width: calc(100% - 24px);
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 10px;
  color: #fff;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 14px;
  background: linear-gradient(110deg, rgba(24, 24, 26, 0.9), rgba(69, 13, 23, 0.78));
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.26);
  backdrop-filter: blur(18px);
}

.watch-live-icon {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 50%;
  background: rgba(255, 77, 94, 0.14);
}

.watch-live-copy {
  min-width: 0;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  line-height: 1.15;
}

.watch-live-copy strong {
  overflow: hidden;
  font-size: 0.72rem;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.watch-live-copy small {
  overflow: hidden;
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.66);
  font-size: 0.59rem;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.watch-live-value {
  flex: 0 0 auto;
  color: #fff;
  font-size: 1.18rem;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.watch-live-value small {
  color: #ff8b98;
  font-size: 0.55rem;
  letter-spacing: 0.04em;
}

.watch-zone-1 { border-left: 3px solid #37d7ff; }
.watch-zone-2 { border-left: 3px solid #00c763; }
.watch-zone-3 { border-left: 3px solid #ffd54f; }
.watch-zone-4 { border-left: 3px solid #ff9800; }
.watch-zone-5 { border-left: 3px solid #ff3b4d; }
</style>
